import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import { SR_API_ACTION_LANDLORD_SIGNUP } from 'api/actionTypes';

import { DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS } from 'common/dataTableConstants';

import { SCREEN_ID_PARTNER_MARKET_LISTING_INVITE_OWNER } from 'client/constants/screenIds';

import { SR_ERROR_USER_ALREADY_EXISTS } from 'common/errorCodes';

import { getListingFullAddressString } from 'common/listingHelpers';

import { hasRoleAnonymous, hasRoleLandlord } from 'common/userRoles';

import {
  dataTable,
  actionInitializeDataTableState,
  actionDataTableLoadItemsIfNotFullyLoaded,
} from 'client/helpers/dataTables';
import log from 'client/lib/log';
import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withApiState from 'client/helpers/withApiState';
import withScrollToTop from 'client/helpers/withScrollToTop';
import postWithState from 'client/actions/postWithState';

import { onLogicSuccess } from 'client/actions/Auth/login';
import PartnerActions from 'client/actions/Partner';

import SrMultiStepForm, {
  SR_MULTI_STEP_FORM_THEME_PARTNER_PAGE,
} from 'client/components/SrMultiStepForm';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrPageFooter from 'client/components/SrPartnerMarketListingForm/SrPageFooter';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrPartnerMarketListingFormStepSimple from 'client/components/SrPartnerMarketListingForm/SrPartnerMarketListingFormStepSimple';

function makeFormStepsGetterFunc({ partner, marketingPartnerListing, landlordSignupApiState }) {
  const listingAddress = getListingFullAddressString(marketingPartnerListing.listing);
  const loginUrl = `/login?ownerInvited=${marketingPartnerListing.landlordId}&listingId=${marketingPartnerListing.listingId}`; // eslint-disable-line max-len
  const showAlreadyHaveAnAccountError =
    landlordSignupApiState?.error?.name === SR_ERROR_USER_ALREADY_EXISTS ||
    landlordSignupApiState?.error?.constraint === 'users_email_unique';

  const steps = [
    {
      id: 'signup',
      component: SrPartnerMarketListingFormStepSimple,
      ui: {
        navbarTitle: listingAddress,
        pageHeader: 'Signup as an Owner',
        pageIntroText: `${partner.displayName} invited you to list ${listingAddress}`,
        theme: SR_MULTI_STEP_FORM_THEME_PARTNER_PAGE,
        hideProgressBar: true,
        hideBackButton: true,
        footerComponent: () => (
          <SrBox>
            {showAlreadyHaveAnAccountError && (
              <SrErrorMessage marginTop="20px">
                It looks like you already have an account. Please
                <SrLink to={loginUrl}>
                  <SrText weight="600" color="white" decoration="underline" size="14">
                    {' log '}
                  </SrText>
                </SrLink>
                in to continue.
              </SrErrorMessage>
            )}
            {landlordSignupApiState?.error && !showAlreadyHaveAnAccountError && (
              <SrErrorMessage marginTop="20px">
                {landlordSignupApiState.error.message}
              </SrErrorMessage>
            )}
            <SrFlex direction="col" justify="center" align="center" marginTop="26px">
              <SrText font="greycliff" weight="500" color="blueGreycliff">
                Already have an account?
              </SrText>
              <SrLink to={loginUrl}>
                <SrText font="greycliff" weight="500" color="blue300">
                  Log in to claim my property
                </SrText>
              </SrLink>
            </SrFlex>
          </SrBox>
        ),
        controlComponent: SrPageFooter,
        controlButtonText: 'Sign Up',
      },
      fieldIds: ['name', 'email', 'phone', 'password', 'confirmPassword'],
    },
  ];

  return () => {
    return steps;
  };
}

function getFormFields() {
  return [
    {
      id: 'name',
      title: 'Full Name',
      type: 'short_text',
      validations: ['required'],
    },
    {
      id: 'email',
      title: 'Email',
      type: 'email',
      validations: ['required', 'email'],
    },
    {
      id: 'phone',
      title: 'Phone',
      type: 'phone',
      validations: ['required', 'phone'],
    },
    {
      id: 'password',
      title: 'New Password',
      type: 'password',
      validations: ['required'],
      properties: {
        placeholder: 'New Password',
      },
    },
    {
      id: 'confirmPassword',
      title: 'Confirm Password',
      type: 'password',
      validations: ['required', 'confirmPassword:password'],
      properties: {
        placeholder: 'Confirm Password',
      },
    },
  ];
}

function getMarketingListingDataTableSettings({ match }) {
  const { marketingPartnerListingId, partnerSlug } = match.params;

  if (!marketingPartnerListingId) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'marketingPartnerListing',
    dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
    viewId: `marketing-partner-listing-id-${marketingPartnerListingId}`,
    count: 1,
    injectSingleItem: true,
    filter: {
      'partners.slug': partnerSlug,
      'marketingPartnerListings.id': parseInt(marketingPartnerListingId, 10),
    },
    sort: {
      'marketingPartnerListings.createdDate': 'desc',
    },
  };
  return dataTableSettings;
}

@withTransitionHook
@withScreenId(SCREEN_ID_PARTNER_MARKET_LISTING_INVITE_OWNER)
@withScrollToTop
@withApiState({
  landlordSignupApiState: {
    action: SR_API_ACTION_LANDLORD_SIGNUP,
  },
})
@branch(({ match }) => ({
  currentUser: ['currentUser'],
  partner: ['partners', 'bySlug', match.params.partnerSlug],
}))
@dataTable(getMarketingListingDataTableSettings)
class MarketingPartnerListingInviteLandlord extends Component {
  static fetchData(match, inject) {
    const partnersBySlug = inject.state.get(['partners', 'bySlug']);
    const promises = [];

    if (!partnersBySlug[match.params.partnerSlug]) {
      promises.push(
        PartnerActions.loadPartnerBySlug(inject, {
          slug: match.params.partnerSlug,
        }),
      );
    }

    if (match.params.marketingPartnerListingId) {
      const dataTableSettings = getMarketingListingDataTableSettings({ match });
      actionInitializeDataTableState(inject, dataTableSettings);
      promises.push(actionDataTableLoadItemsIfNotFullyLoaded(inject, dataTableSettings));
    }

    return Promise.all(promises);
  }

  static getPageMetadata = () => ({
    pageTitle: 'Signup as an Owner',
  });

  static propTypes = {
    isTransitioning: PropTypes.bool.isRequired,
    partner: PropTypes.object,
    marketingPartnerListing: PropTypes.object,
    landlordSignupApiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  constructor() {
    super();

    this.state = {
      isLoading: false,
    };
  }

  _onSubmit = (formValues) => {
    const { dispatch, marketingPartnerListing } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_LANDLORD_SIGNUP,
      payload: {
        name: formValues.name,
        email: formValues.email,
        phone: formValues.phone,
        password: formValues.password,
        listingId: marketingPartnerListing.listingId,
        landlordId: marketingPartnerListing.landlordId,
      },
    })
      .then(({ user }) => {
        this.setState({ isLoading: true });

        return dispatch(({ state, apiClient }) =>
          onLogicSuccess({
            state,
            apiClient,
            user,
          }),
        );
      })
      .catch((error) => {
        log.error(error);
      });
  };

  render() {
    const {
      currentUser,
      isTransitioning,
      partner,
      marketingPartnerListing,
      landlordSignupApiState,
      dispatch,
    } = this.props;

    if (isTransitioning && (!partner || !marketingPartnerListing)) {
      return null;
    }

    const landlordId = marketingPartnerListing.landlordId;

    if (!hasRoleAnonymous(currentUser.roles)) {
      if (hasRoleLandlord(currentUser.roles) && currentUser.landlordId === landlordId) {
        return (
          <Redirect to={`/partner/${partner.slug}/market-property/${marketingPartnerListing.id}`} />
        );
      }
      return (
        <Redirect
          to={`/logout?next=${`/partner/${partner.slug}/market-property/${marketingPartnerListing.id}/invite/${landlordId}`}`} // eslint-disable-line max-len
        />
      );
    }

    return (
      <>
        <SrMultiStepForm
          getFormSteps={makeFormStepsGetterFunc({
            partner,
            marketingPartnerListing,
            landlordSignupApiState,
          })}
          getFormFields={getFormFields}
          onSubmit={this._onSubmit}
          defaultValues={{
            name: marketingPartnerListing.landlordName,
            email: marketingPartnerListing.landlordEmail,
            phone: marketingPartnerListing.landlordPhone,
          }}
          dispatch={dispatch}
          partner={partner}
          confirmLeave={false}
          isBusy={landlordSignupApiState.inProgress || this.state.isLoading}
          validateOnClickNext
        />
      </>
    );
  }
}

MarketingPartnerListingInviteLandlord.displayName = 'MarketingPartnerListingInviteLandlord';

export default MarketingPartnerListingInviteLandlord;
